import * as React from 'react';

import { GetCatalogProductViewQuery } from '@notino/shared/definitions/types';

import { getGiftCampaigns } from '@lib/gift';

import { useCartServices } from './ProductDetail/hooks/useCartServices';
import { useCurrentVariant } from './ProductDetail/hooks/useCurrentVariant';
import {
  useVariantGroups,
  UseVariantGroupsReturn,
} from './ProductDetail/ProductBaseInfo/VariantGrouping/useVariantGroups';
import {
  createProductSectionsStore,
  ProductSectionsStoreContextProvider,
} from './useProductSectionsStateInfo';

type Product = NonNullable<
  GetCatalogProductViewQuery['productDetailByCatalogMasterId']
>;

interface ProductDetailContextValue extends ReturnType<typeof useCartServices> {
  product: Product;
  currentVariant: Product['variants'][number];
  customVersionKey?: string;
  shopSettings: GetCatalogProductViewQuery['shopSettings'];
  currentCurrency: GetCatalogProductViewQuery['currentCurrency'];
  currentLocale: GetCatalogProductViewQuery['currentLocale'];
  variantGroups: UseVariantGroupsReturn;
}

interface Props {
  customVersionKey?: string | null;
  PDView: Partial<GetCatalogProductViewQuery>;
  [key: string]: unknown;
}

const Context = React.createContext<ProductDetailContextValue>(
  null as unknown as ProductDetailContextValue
);

export const useProductDetailContext = (throwError = true) => {
  const value = React.useContext(Context);
  if (!value && throwError) {
    throw Error(
      'useProductDetailContext must be within ProductDetailContextProvider.'
    );
  }

  return value;
};

export const ProductDetailContextProvider = ({
  children,
  PDView: {
    productDetailByCatalogMasterId: _product,
    shopSettings,
    currentCurrency,
    currentLocale,
  },
  customVersionKey,
}: React.PropsWithChildren<Props>) => {
  const product = React.useMemo(() => {
    if (!_product) {
      return _product as unknown as Product;
    }
    return {
      ..._product,
      variants: _product.variants.map((variant) => ({
        ...variant,
        attributes: {
          ...variant.attributes,
          Gift: getGiftCampaigns({ product: _product, variant } as any)
            .hasGiftCampaigns,
        },
      })),
    };
  }, [_product]);
  const currentVariant = useCurrentVariant(
    product?.variants ?? []
  ) as NonNullable<ReturnType<typeof useCurrentVariant>>;
  const cartServices = useCartServices(product, currentVariant);
  const variantGroups = useVariantGroups({ product, currentVariant });

  const productSectionsStoreRef = React.useRef(createProductSectionsStore());

  const value = React.useMemo(
    () =>
      ({
        product,
        currentVariant,
        ...cartServices,
        customVersionKey,
        shopSettings,
        currentCurrency,
        currentLocale,
        variantGroups,
      } as ProductDetailContextValue),
    [
      product,
      currentVariant,
      cartServices,
      customVersionKey,
      shopSettings,
      currentCurrency,
      currentLocale,
      variantGroups,
    ]
  );

  return (
    <ProductSectionsStoreContextProvider
      value={productSectionsStoreRef.current}
    >
      <Context.Provider value={value}>{children}</Context.Provider>
    </ProductSectionsStoreContextProvider>
  );
};
